import { OAuthClientCredentials, Server } from '@nextforce/api'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { makeStyles, Text, ThemeProvider } from '@rneui/themed'
import getEnvironment from 'app/environment'
import { getDeviceToken } from 'app/server/device_token'
import { getClientEnvironment, getClientPlatform } from 'app/server/platform'
import { safeErrorMessage } from 'shared/error'

import { appTheme } from 'app/theme'
import React, {
  createContext,
  JSX,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { AppState, View } from 'react-native'
import Loading from 'shared/components/loading'
import { RoundedButton } from 'shared/components/rounded-button'
import { EnvName } from 'users/account/dev_tools/types'
import { MobileAppAuthenticator } from './authenticator'

// Valid environment names used for schema validation
const VALID_ENV_NAMES: EnvName[] = [
  'development',
  'staging',
  'production_internal',
  'staging_production_internal',
  'production',
  'custom',
]

async function credentials(): Promise<OAuthClientCredentials> {
  return {
    deviceToken: await getDeviceToken(),
    platform: getClientPlatform(),
    env: getClientEnvironment(),
  }
}

export type ServerContextValue = {
  server: Server
  accessToken: string | undefined
  localBaseURLInfo: BaseURLInfo | undefined
  onReset: () => Promise<void>
}

export const LOCAL_BASE_URL_INFO_KEY = 'local_base_url_info'

export const ServerContext = createContext({} as ServerContextValue)

export interface BaseURLInfo {
  envName: EnvName
  endpoint: string
}

interface Props {
  children: React.ReactNode
}

export function ServerProvider(props: Props) {
  const { name, SERVER_API_URL } = getEnvironment()
  const [server, setServer] = useState<Server>()
  const [authenticator, setAuthenticator] = useState<MobileAppAuthenticator>()
  const [accessToken, setAccessToken] = useState<string | undefined>()
  const [localBaseURLInfo, setLocalBaseURLInfo] = useState<BaseURLInfo>()
  const [secureStoreError, setSecureStoreError] = useState<string | undefined>()

  const onLoadTokensError = useCallback(
    (e: Error) => {
      if (!secureStoreError) {
        const message = safeErrorMessage(e)
        console.error('Error loading tokens:', message)
        setSecureStoreError(message)
      }
    },
    [secureStoreError]
  )

  const instantiateAuthenticator = useCallback(async () => {
    if (localBaseURLInfo) {
      const newAuthenticator = new MobileAppAuthenticator(
        localBaseURLInfo.endpoint,
        await credentials(),
        setAccessToken,
        onLoadTokensError
      )
      return newAuthenticator
    }
  }, [localBaseURLInfo, onLoadTokensError])

  const configureAuthenticator = useCallback(async () => {
    const newAuthenticator = await instantiateAuthenticator()

    if (newAuthenticator && !secureStoreError) {
      try {
        await newAuthenticator.loadTokens()
        setAuthenticator(newAuthenticator)
      } catch (e) {
        onLoadTokensError(e)
      }
    }
  }, [instantiateAuthenticator, onLoadTokensError, secureStoreError])

  const configureBaseUrlInfo = useCallback(async () => {
    const baseURLJSONValue = await AsyncStorage.getItem(LOCAL_BASE_URL_INFO_KEY)
    let baseURLInfo: BaseURLInfo | undefined | null = null

    // Try to parse stored value, but guard against corrupted/invalid data
    if (baseURLJSONValue !== null) {
      try {
        const parsed = JSON.parse(baseURLJSONValue)
        // Validate parsed structure: endpoint must be string, envName must be valid channel
        if (
          typeof parsed?.endpoint === 'string' &&
          VALID_ENV_NAMES.includes(parsed?.envName)
        ) {
          baseURLInfo = parsed
        } else {
          console.warn(
            'Stored baseURLInfo has invalid schema, resetting:',
            parsed
          )
          await AsyncStorage.removeItem(LOCAL_BASE_URL_INFO_KEY)
          baseURLInfo = null
        }
      } catch (e) {
        console.warn('Failed to parse stored baseURLInfo, resetting:', e)
        // Remove corrupted data so app can recover automatically
        await AsyncStorage.removeItem(LOCAL_BASE_URL_INFO_KEY)
        baseURLInfo = null
      }
    }

    // Build current environment's base URL info
    // All release channels are preserved; only user overrides are marked 'custom'
    const currentBaseURLInfo: BaseURLInfo = {
      endpoint: SERVER_API_URL,
      envName: (name as EnvName) || 'custom',
    }

    // Update stored URL if:
    // 1. No stored URL exists, OR
    // 2. Stored URL differs from current environment AND it's not a custom override
    // (Custom overrides persist. Non-custom stale endpoints always get corrected to current env.)
    if (
      !baseURLInfo?.endpoint ||
      (baseURLInfo.endpoint !== SERVER_API_URL &&
        baseURLInfo.envName !== 'custom')
    ) {
      baseURLInfo = currentBaseURLInfo
      await AsyncStorage.setItem(
        LOCAL_BASE_URL_INFO_KEY,
        JSON.stringify(baseURLInfo)
      )
    }

    if (!baseURLInfo) {
      throw new Error('Could not configure server')
    }

    setLocalBaseURLInfo(baseURLInfo)
  }, [SERVER_API_URL, name])

  const configureServer = useCallback(async () => {
    if (authenticator && localBaseURLInfo && !secureStoreError) {
      const newServer = new Server(localBaseURLInfo.endpoint, authenticator)
      setServer(newServer)
    }
  }, [authenticator, localBaseURLInfo, secureStoreError])

  const onReset = useCallback(async () => {
    setSecureStoreError(undefined)
    setServer(undefined)
    setAuthenticator(undefined)
    await configureAuthenticator()
    await configureServer()
  }, [configureAuthenticator, configureServer])

  const onDeauthenticate = useCallback(async () => {
    if (authenticator) {
      await authenticator.deauthenticate()
    } else {
      const newAuthenticator = await instantiateAuthenticator()
      if (newAuthenticator) {
        await newAuthenticator.deauthenticate()
      }
    }
    await onReset()
  }, [authenticator, instantiateAuthenticator, onReset])

  useEffect(() => {
    if (!localBaseURLInfo) {
      configureBaseUrlInfo().catch((e: unknown) => {
        console.error('Error configuring base url info:', safeErrorMessage(e))
      })
    }
  }, [configureBaseUrlInfo, localBaseURLInfo])

  useEffect(() => {
    if (!authenticator) {
      configureAuthenticator().catch((e: unknown) => {
        console.error('Error configuring authenticator:', safeErrorMessage(e))
      })
    }
  }, [authenticator, configureAuthenticator, secureStoreError])

  useEffect(() => {
    if (!server && !secureStoreError) {
      configureServer().catch((e: unknown) => {
        console.error('Error configuring server:', safeErrorMessage(e))
      })
    }
  }, [
    authenticator,
    configureServer,
    localBaseURLInfo,
    secureStoreError,
    server,
  ])

  useEffect(() => {
    const sub = AppState.addEventListener('change', async nextAppState => {
      if (nextAppState === 'active' && secureStoreError) {
        await onReset()
      }
    })
    return () => {
      sub.remove()
    }
  }, [onReset, secureStoreError])

  const DefaultThemeProvider = (child: JSX.Element) => (
    <ThemeProvider theme={appTheme}>{child}</ThemeProvider>
  )

  if (secureStoreError) {
    return DefaultThemeProvider(
      <ErrorView
        error={secureStoreError}
        onDeauthenticate={onDeauthenticate}
        onReset={onReset}
      />
    )
  }

  if (!server) {
    return DefaultThemeProvider(<Loading />)
  }

  const value: ServerContextValue = {
    server,
    accessToken,
    localBaseURLInfo,
    onReset,
  }

  return (
    <ServerContext.Provider value={value}>
      {props.children}
    </ServerContext.Provider>
  )
}

export function useServer() {
  return useContext<ServerContextValue>(ServerContext)
}

const ErrorView = ({
  error,
  onDeauthenticate,
  onReset,
}: {
  error: string
  onReset: () => Promise<void>
  onDeauthenticate: () => Promise<void>
}) => {
  const styles = useStyles()
  return (
    <View style={styles.container}>
      <Text style={styles.errorText}>{error}</Text>
      <View style={styles.buttonRow}>
        <RoundedButton
          title="Retry"
          onPress={async () => {
            await onReset()
          }}
          containerStyle={styles.buttonContainer}
        />
        <RoundedButton
          title="Logout"
          danger
          onPress={() => onDeauthenticate()}
          containerStyle={styles.buttonContainer}
        />
      </View>
    </View>
  )
}

const useStyles = makeStyles(() => ({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  errorText: { marginBottom: 10 },
  buttonRow: { flexDirection: 'row' },
  buttonContainer: { margin: 5 },
}))
